"use client";

import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import {
	AppBar,
	Box,
	Dialog,
	DialogContent,
	type DialogProps,
	Divider,
	IconButton,
	Image,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Toolbar,
	Typography,
} from "@/components/controls/base-controls";
import { CloseIcon } from "@/components/controls/icons";
import { HEADER_HEIGHT_MOBILE } from "@/core/constants/webpages";
import type { AppModule } from "@/core/types/app-module.type";
import { Link } from "@/i18n/navigation";

const AppsDialog = ({
	appModules,
	open,
	onHandleDialogClose,
	scroll="body",
}: {
	appModules: AppModule[];
	open: boolean;
	onHandleDialogClose: () => void;
	scroll: DialogProps["scroll"];
}) => {
	const t = useTranslations();
	const router = useRouter();

	const handleClose = () => {
		try {
			onHandleDialogClose();
		} catch (error) {
			console.error("Error handling dialog close:", error);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullScreen
			disableRestoreFocus={true}
			aria-labelledby="dialog-title"
			aria-describedby="dialog-description"
		>
			<AppBar
				id="dialog-title"
				sx={{
					backgroundColor: "var(--mui-palette-darkest)",
					padding: 0,
				}}
			>
				<Toolbar
					variant="dense"
					sx={{
						height: HEADER_HEIGHT_MOBILE,
						width: "100%",
						display: "flex",
						flexDirection: "row",
						justifyContent: "space-between",
						alignItems: "center",
						margin: "0 auto",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Image
							src="/images/logo.png"
							alt="Navairra Logo"
							loading="eager"
							width={44}
							height={31}
							style={{
								filter: "brightness(0) invert(1)",
							}}
						/>
						<Link
							href="/home"
							style={{
								textDecoration: "none",
								whiteSpace: "pre-line",
							}}
							onClick={() => {
								try {
									handleClose();
								} catch (error) {
									console.error("Error navigating:", error);
								}
							}}
						>
							<Typography
								variant="companyName"
								sx={{
									color: "var(--mui-palette-lightest)",
								}}
							>
								{t("company.name")}
							</Typography>
						</Link>

						<Divider
							orientation="vertical"
							flexItem
							sx={{
								borderColor: "var(--mui-palette-dull)",
								height: "2em",
								borderRightWidth: "2px",
								my: "auto",
							}}
						/>

						<Typography
							variant="tagLine"
							sx={{
								color: "var(--mui-palette-light)",
								mt: { xs: "4px", md: "3px" },
							}}
						>
							{t("company.tagLine1")}
						</Typography>
					</Box>

					<IconButton
						edge="end"
						color="inherit"
						onClick={handleClose}
						aria-label="close"
						sx={{ position: "absolute", right: 24, top: 12, zIndex: 1 }}
					>
						<CloseIcon />
					</IconButton>
				</Toolbar>
			</AppBar>

			<DialogContent
				id="dialog-description"
				dividers={scroll === "paper"}
				sx={{
					backgroundColor: "var(--mui-palette-dark)",
					color: "var(--mui-palette-lightest)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: 0,
				}}
			>
				<List sx={{ p: 0 }}>
					{appModules.map((item) => {
						const showDivider = item.id === "reg";
						return (
							<ListItem
								key={item.id}
								disablePadding
								sx={
									showDivider
										? {
												borderBottom: "1px solid",
												borderColor: "var(--mui-palette-light)",
												mb: "1em",
												pb: ".75em",
												width: "100%",
											}
										: undefined
								}
							>
								<ListItemButton
									onClick={() => {
										try {
											handleClose();
											router.push(item.path);
										} catch (error) {
											console.error("Error navigating:", error);
										}
									}}
								>
									<ListItemText
										primary={t(item.label).toUpperCase()}
										slotProps={{
											primary: { variant: "body1", align: "center" },
										}}
									/>
								</ListItemButton>
							</ListItem>
						);
					})}
				</List>
			</DialogContent>
		</Dialog>
	);
};

export default AppsDialog;
